import React, { useState, useEffect } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';

interface EnhancedTestModalProps {
  api: any;
  onTest: (testValue: string, useTestMode: boolean) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  testResult: any;
}

export const EnhancedTestModal: React.FC<EnhancedTestModalProps> = ({ 
  api, 
  onTest, 
  onClose, 
  isOpen, 
  testResult 
}) => {
  const [testValue, setTestValue] = useState('');
  const [useTestMode, setUseTestMode] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [testHistory, setTestHistory] = useState<any[]>([]);
  const [autoTest, setAutoTest] = useState(false);
  const [testInterval, setTestInterval] = useState(5000); // 5 seconds

  // Load test history from localStorage
  useEffect(() => {
    if (api?.id) {
      const history = localStorage.getItem(`test_history_${api.id}`);
      if (history) {
        setTestHistory(JSON.parse(history));
      }
    }
  }, [api?.id]);

  // Auto-test functionality
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (autoTest && testValue && !isTesting) {
      interval = setInterval(() => {
        handleTest();
      }, testInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoTest, testValue, isTesting, testInterval]);

  // Real-time validation
  useEffect(() => {
    if (testValue && api?.searchParameterType) {
      validateTestValue(testValue);
    } else {
      setValidationError('');
    }
  }, [testValue, api?.searchParameterType]);

  const validateTestValue = (value: string) => {
    if (!api?.searchParameterType) return;
    
    try {
      const regex = new RegExp(api.searchParameterType.validation_regex);
      if (!regex.test(value)) {
        setValidationError(api.searchParameterType.validation_error_message || 'Invalid format');
      } else {
        setValidationError('');
      }
    } catch (error) {
      setValidationError('Invalid regex pattern');
    }
  };

  const handleTest = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!api || !testValue.trim()) return;
    
    setIsTesting(true);
    try {
      await onTest(testValue, useTestMode);
      
      // Add to test history
      const newTest = {
        timestamp: new Date().toISOString(),
        testValue,
        useTestMode,
        result: testResult
      };
      
      const updatedHistory = [newTest, ...testHistory.slice(0, 9)]; // Keep last 10 tests
      setTestHistory(updatedHistory);
      
      // Save to localStorage
      if (api.id) {
        localStorage.setItem(`test_history_${api.id}`, JSON.stringify(updatedHistory));
      }
    } finally {
      setIsTesting(false);
    }
  };

  const getStatusColor = (success: boolean) => {
    return success ? 'text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30' : 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30';
  };

  const getStatusIcon = (success: boolean) => {
    return success ? '✅' : '❌';
  };

  const formatTestValue = (value: string) => {
    if (api?.searchParameterType?.name === 'phone_number') {
      return value.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Enhanced API Testing: {api?.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:text-gray-300 text-2xl"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Test Configuration */}
          <div className="space-y-6">
            <Card>
              <div className="p-4">
                <h3 className="text-lg font-semibold mb-4">Test Configuration</h3>
                
                <form onSubmit={handleTest} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
                      Test Value *
                    </label>
                    <input
                      type="text"
                      value={testValue}
                      onChange={(e) => setTestValue(e.target.value)}
                      className={`w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        validationError ? 'border-red-300' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      placeholder={`Enter ${api?.searchParameterType?.display_name || 'test'} value...`}
                      required
                    />
                    {validationError && (
                      <p className="text-red-600 dark:text-red-400 text-sm mt-1">{validationError}</p>
                    )}
                    {api?.searchParameterType?.validation_regex && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Format: {api.searchParameterType.validation_regex}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={useTestMode}
                        onChange={(e) => setUseTestMode(e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-200">Use Test Mode (No credit deduction)</span>
                    </label>
                  </div>

                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={autoTest}
                        onChange={(e) => setAutoTest(e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 focus:ring-indigo-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-200">Auto Test</span>
                    </label>
                    
                    {autoTest && (
                      <select
                        value={testInterval}
                        onChange={(e) => setTestInterval(parseInt(e.target.value))}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1"
                      >
                        <option value={5000}>5 seconds</option>
                        <option value={10000}>10 seconds</option>
                        <option value={30000}>30 seconds</option>
                        <option value={60000}>1 minute</option>
                      </select>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="submit"
                      disabled={isTesting || !testValue.trim() || !!validationError}
                      className="flex-1"
                    >
                      {isTesting ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Testing...
                        </span>
                      ) : (
                        'Test API'
                      )}
                    </Button>
                    <Button variant="outline" onClick={onClose}>
                      Close
                    </Button>
                  </div>
                </form>
              </div>
            </Card>

            {/* Test History */}
            {testHistory.length > 0 && (
              <Card>
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-4">Test History</h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {testHistory.map((test, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-900 rounded"
                      >
                        <div className="flex items-center space-x-2">
                          <span className={getStatusIcon(test.result?.success)}>
                            {test.result?.success ? '✅' : '❌'}
                          </span>
                          <span className="text-sm font-mono">
                            {formatTestValue(test.testValue)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(test.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}
          </div>

          {/* Right Column - Test Results */}
          <div className="space-y-6">
            {testResult && (
              <Card>
                <div className="p-4">
                  <h3 className="text-lg font-semibold mb-4">Test Results</h3>
                  
                  {/* Status Summary */}
                  <div className={`p-4 rounded-lg border mb-4 ${getStatusColor(testResult.success)}`}>
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getStatusIcon(testResult.success)}</span>
                      <div>
                        <h4 className="font-semibold">
                          {testResult.success ? 'Test Successful' : 'Test Failed'}
                        </h4>
                        <p className="text-sm">
                          API: {testResult.api_name} | 
                          Status: {testResult.response_status} | 
                          Time: {testResult.response_time?.toFixed(2)}s
                        </p>
                        {testResult.error && (
                          <p className="text-sm mt-1 font-medium">{testResult.error}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Request Details */}
                  <details className="mb-4">
                    <summary className="cursor-pointer font-semibold mb-2 flex items-center">
                      <span className="mr-2">📤</span>
                      Request Details
                    </summary>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm">
                      <div className="space-y-2">
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-300">URL:</span>
                          <p className="font-mono bg-white dark:bg-gray-800 p-2 rounded border break-all">
                            {testResult.request?.url}
                          </p>
                        </div>
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-300">Method:</span>
                          <Badge variant="outline" className="ml-2">
                            {testResult.request?.method}
                          </Badge>
                        </div>
                        {testResult.request?.headers && Object.keys(testResult.request.headers).length > 0 && (
                          <div>
                            <span className="font-medium text-gray-600 dark:text-gray-300">Headers:</span>
                            <pre className="text-xs bg-white dark:bg-gray-800 border rounded p-2 overflow-auto max-h-32">
                              {JSON.stringify(testResult.request.headers, null, 2)}
                            </pre>
                          </div>
                        )}
                        {testResult.request?.data && Object.keys(testResult.request.data).length > 0 && (
                          <div>
                            <span className="font-medium text-gray-600 dark:text-gray-300">Data:</span>
                            <pre className="text-xs bg-white dark:bg-gray-800 border rounded p-2 overflow-auto max-h-32">
                              {JSON.stringify(testResult.request.data, null, 2)}
                            </pre>
                          </div>
                        )}
                      </div>
                    </div>
                  </details>

                  {/* Response Details */}
                  <details>
                    <summary className="cursor-pointer font-semibold mb-2 flex items-center">
                      <span className="mr-2">📥</span>
                      Response Details
                    </summary>
                    <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded text-sm">
                      <div className="space-y-2">
                        <div className="flex items-center space-x-4">
                          <div>
                            <span className="font-medium text-gray-600 dark:text-gray-300">Status Code:</span>
                            <Badge 
                              variant={testResult.response_status >= 200 && testResult.response_status < 300 ? 'success' : 'danger'}
                              className="ml-2"
                            >
                              {testResult.response_status}
                            </Badge>
                          </div>
                          <div>
                            <span className="font-medium text-gray-600 dark:text-gray-300">Response Time:</span>
                            <span className="ml-2">{testResult.response_time?.toFixed(2)}s</span>
                          </div>
                        </div>
                        
                        {testResult.response?.headers && Object.keys(testResult.response.headers).length > 0 && (
                          <div>
                            <span className="font-medium text-gray-600 dark:text-gray-300">Response Headers:</span>
                            <pre className="text-xs bg-white dark:bg-gray-800 border rounded p-2 overflow-auto max-h-32">
                              {JSON.stringify(testResult.response.headers, null, 2)}
                            </pre>
                          </div>
                        )}
                        
                        <div>
                          <span className="font-medium text-gray-600 dark:text-gray-300">Response Data:</span>
                          <pre className="text-xs bg-white dark:bg-gray-800 border rounded p-2 overflow-auto max-h-64">
                            {typeof testResult.response?.data === 'string' 
                              ? testResult.response.data 
                              : JSON.stringify(testResult.response?.data, null, 2)
                            }
                          </pre>
                        </div>
                      </div>
                    </div>
                  </details>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
